<template>
  <teleport to="#wishlist-modals">
    <SignUpModal
      v-if="signUpModal"
      title="Sign up to save this space"
      :tracking-params="{ context: 'add_to_wishlist', auth_method: 'email_password', country }"
      :api-config="apiConfig"
      :google-api-config="googleApiConfig"
      :apple-api-config="appleApiConfig"
      :email="email"

      @register="onSignUpModalRegister"
      @error="onSignUpModalError"
      @close="onSignUpModalClose"
      @trackEvent="onSignUpModalTrackEvent"
      @loginClick="onSignUpModalLoginClick"
    />

    <WelcomeBackLoginModal
      v-if="welcomeBackLoginModal"
      :forgot-password-url="forgotPasswordUrl"
      :magic-link-redirect-url="magicLinkRedirectUrl"
      :sign-up="true"
      :tracking-params="{ context: 'add_to_wishlist', auth_method: 'logged_in', country }"
      :api-config="apiConfig"
      :google-api-config="googleApiConfig"
      :apple-api-config="appleApiConfig"
      :email="email"
      top-text="Please log in to save this space."
      @login="onWelcomeBackLoginModalLogin"
      @error="onWelcomeBackLoginModalError"
      @close="onWelcomeBackLoginModalClose"
      @trackEvent="onWelcomeBackLoginModalTrackEvent"
      @signUpClick="onWelcomeBackLoginModalSignUpClick"
    />
  </teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';

import SignUpModal from '@/modules/user-cabinet/user-cabinet/BookingForm/SignUpModal.vue';
import WelcomeBackLoginModal from '@/modules/user-cabinet/user-cabinet/BookingForm/WelcomeBackLoginModal.vue';
import { useToaster } from '@/services/toaster';
import { WISHLIST_LOCAL_STORAGE_KEY, WISHLIST_ROOM_ID_QUERY_PARAM } from '@/services/wishlist';

import WishlistToastContent from './WishlistToastContent.vue';

defineOptions({ name: 'WishlistModals' });

const {
  forgotPassword: forgotPasswordUrl,
  apiConfig,
  googleApiConfig,
  appleApiConfig,
  country,
} = window._VUE_WISHLIST_MODAL;

const { showNotification } = useToaster();

const roomId = ref(null);
const signUpModal = ref(false);
const welcomeBackLoginModal = ref(false);
const email = ref('');

const magicLinkRedirectUrl = computed(() => {
  const params = new URLSearchParams(window.location.search);
  params.set(WISHLIST_ROOM_ID_QUERY_PARAM, roomId.value);

  return `${window.location.pathname}?${params.toString()}`;
});

const showErrorMessage = (error) => {
  showNotification.error(error);
};

const showRoomMessage = (message, room) => {
  showNotification({
    type: 'success',
    // The thumbnail already says which space the message is about, so the type icon beside it
    // only adds noise.
    hideIcon: !!room?.photo,
    content: WishlistToastContent,
    contentProps: { message, room },
  });
};

const refreshWithParam = () => {
  window.sessionStorage.setItem(WISHLIST_LOCAL_STORAGE_KEY, roomId.value);
  window.location.reload();
};

const onSignUpModalClose = () => {
  signUpModal.value = false;
};

const onSignUpModalLoginClick = (enteredEmail) => {
  onSignUpModalClose();
  email.value = enteredEmail;
  nextTick(() => {
    welcomeBackLoginModal.value = true;
  });
};

const onSignUpModalTrackEvent = () => {};

const onSignUpModalError = showErrorMessage;

const onSignUpModalRegister = refreshWithParam;

const onWelcomeBackLoginModalLogin = refreshWithParam;

const onWelcomeBackLoginModalClose = () => {
  welcomeBackLoginModal.value = false;
};

const onWelcomeBackLoginModalTrackEvent = () => {};

const onWelcomeBackLoginModalSignUpClick = (enteredEmail) => {
  onWelcomeBackLoginModalClose();
  email.value = enteredEmail;
  nextTick(() => {
    signUpModal.value = true;
  });
};

const onWelcomeBackLoginModalError = showErrorMessage;

onMounted(() => {
  window.triggerWishlistModal = (id) => {
    if (document.cookie.includes('tgv_returning_client=')) {
      welcomeBackLoginModal.value = true;
    } else {
      signUpModal.value = true;
    }
    roomId.value = id;
  };
  // Also the only confirmation a Client gets after logging in to save a space, so it offers the
  // list rather than leaving them to spot the header counter.
  window.showMessageAboutSuccessfulAddingToWishlist = (room = null) => {
    showRoomMessage('Saved to favourites', room);
  };
  window.showMessageAboutSuccessfulRemovingToWishlist = (room = null) => {
    showRoomMessage('Removed from favourites', room);
  };
});
</script>
